// Admin > Raporlar > Entegrasyonlar: müşteri raporunun veri kaynakları.
// Reklam/analitik kimlikleri, sipariş kaynağı (Pixa / Shopify / Excel-CSV dosyası), web sitesi kataloğu;
// her kaynağın veri kapsamı, geçmiş yükleme durumu ve canlı bağlantı testi.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, SectionHeader, T } from '../poyais-ui';
import { getToken } from '../auth';
import PoyaisSpinner from '../PoyaisSpinner';

const API = '/api/v1/reports/admin/integrations';

interface Cov { start: string; end: string; days?: number; rows?: number; updated?: string }
interface Job { status: 'running' | 'done' | 'error' | ''; started_at: string | null; finished_at: string | null; detail: string }
interface FileImport { id: number; filename: string; start: string; end: string; rows: number; orders: number; revenue: number; created_at: string }
interface Status {
  client: { id: number; name: string };
  fields: { ga4_property_id: string; gads_customer_id: string; meta_account_id: string; meta_token: boolean; meta_own_token: boolean; website: string };
  ga4_oauth: boolean;
  service_account: string;
  coverage: { google_ads: Cov | null; meta_ads: Cov | null; orders: Cov | null };
  orders: {
    source: string; active: string; code_rule: string;
    sources: Record<string, string>; code_rules: Record<string, string>;
    pixa: { connected: boolean; host: string; user: string };
    boyner: { connected: boolean; seller_id: string; connected_at: string | null };
    shopify: { connected: boolean; shop: string; name: string };
    last_run_at: string | null; last_error: string | null;
    imports: FileImport[];
  };
  catalog: { products: number; updated: string | null };
  jobs: Record<string, Job>;
}
interface Check { ok: boolean; message: string }
interface FileResult {
  ok: boolean; dry_run?: boolean; error?: string; headers: string[]; mapping: Record<string, string>;
  fields: Record<string, string>; required: string[]; missing: string[];
  orders?: number; lines?: number; start?: string; end?: string; revenue?: number; net_revenue?: number;
  channels?: string[]; skipped?: Record<string, number>;
  preview?: { date: string; channel: string; code: string; name: string; qty: number; amount: number }[];
}

async function call(path: string, opts: RequestInit = {}) {
  const isForm = opts.body instanceof FormData;
  const res = await fetch(API + path, {
    ...opts,
    headers: { ...(isForm ? {} : { 'Content-Type': 'application/json' }), Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : `İstek başarısız (${res.status})`);
  return j;
}

const inputStyle: React.CSSProperties = {
  height: 40, padding: '0 12px', borderRadius: 10, border: `1px solid ${T.borderMid}`, background: T.surface,
  color: T.text, fontFamily: 'inherit', fontSize: 13, width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: T.textMid };
const hint: React.CSSProperties = { fontSize: 12, color: T.textDim, lineHeight: '18px', margin: 0 };
const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const d = (iso?: string | null) => { if (!iso) return ''; const [y, m, dd] = iso.slice(0, 10).split('-').map(Number); return `${dd} ${MONTHS[m - 1]} ${y}`; };
const ts = (s?: string | null) => (s ? `${d(s)} ${s.slice(11, 16)}` : '');
const tl = (n?: number) => (n == null ? '–' : `₺${Math.round(n).toLocaleString('tr-TR')}`);
const SOURCE_KEYS: Record<string, string> = { google_ads: 'Google Ads', meta_ads: 'Meta Ads', ga4: 'GA4', orders: 'Sipariş verisi', catalog: 'Ürün kataloğu' };

function JobLine({ job }: { job?: Job }) {
  if (!job || !job.status) return null;
  if (job.status === 'running') return <span style={{ fontSize: 12, color: T.amberText, fontWeight: 700 }}>Geçmiş veri yükleniyor… ({ts(job.started_at)} UTC)</span>;
  if (job.status === 'error') return <span style={{ fontSize: 12, color: T.redText }}>Son Yükleme Hatası: {job.detail}</span>;
  return <span style={{ fontSize: 12, color: T.textDim }}>Son Yükleme {ts(job.finished_at)} UTC</span>;
}

function CheckLine({ c }: { c?: Check }) {
  if (!c) return null;
  return <span style={{ fontSize: 12, fontWeight: 600, color: c.ok ? T.greenText : T.redText }}>{c.ok ? '✓ ' : '✕ '}{c.message}</span>;
}

function Row({ title, connected, children }: { title: string; connected: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 0', borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{title}</span>
        {connected ? <Badge color={T.green}>Bağlı</Badge> : <Badge color={T.textDim}>Bağlı değil</Badge>}
      </div>
      {children}
    </div>
  );
}

export default function ReportIntegrations({ clientId, onChanged }: { clientId: number; onChanged?: () => void }) {
  const [st, setSt] = useState<Status | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [checks, setChecks] = useState<Record<string, Check>>({});
  const [ads, setAds] = useState({ ga4_property_id: '', gads_customer_id: '', meta_account_id: '', meta_access_token: '' });
  const [website, setWebsite] = useState('');
  const [pixa, setPixa] = useState({ host: '', email: '', api_token: '' });
  const [boyner, setBoyner] = useState({ seller_id: '', api_key: '', api_secret: '' });
  const [shop, setShop] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fres, setFres] = useState<FileResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  const apply = useCallback((s: Status) => {
    setSt(s);
    setAds({ ga4_property_id: s.fields.ga4_property_id, gads_customer_id: s.fields.gads_customer_id, meta_account_id: s.fields.meta_account_id, meta_access_token: '' });
    setWebsite(s.fields.website);
  }, []);
  const load = useCallback(() => call(`/${clientId}`).then(apply).catch((e) => setErr(e.message)), [clientId, apply]);

  useEffect(() => { setSt(null); setChecks({}); setFres(null); setFile(null); setErr(''); load(); }, [load]);

  // Yükleme sürerken durumu yenile (form alanlarına dokunmadan)
  const running = !!st && Object.values(st.jobs).some((j) => j.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => call(`/${clientId}`).then((s: Status) => setSt(s)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [running, clientId]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setErr('');
    try { await fn(); } catch (e: any) { setErr(e.message); }
    setBusy('');
  };
  const save = (body: object, note: string) => run('save', async () => {
    apply(await call(`/${clientId}`, { method: 'PUT', body: JSON.stringify(body) }));
    flash(note); onChanged?.();
  });

  if (!st) return <Card>{err ? <p style={{ margin: 0, color: T.redText }}>{err}</p> : <PoyaisSpinner size={32} pad={12}/>}</Card>;

  const cov = (c: Cov | null) => (c ? `Veri: ${d(c.start)} – ${d(c.end)}${c.days ? ` · ${c.days} gün` : ''}` : 'Henüz veri yok');
  const adsDirty = ads.ga4_property_id !== st.fields.ga4_property_id || ads.gads_customer_id !== st.fields.gads_customer_id
    || ads.meta_account_id !== st.fields.meta_account_id || !!ads.meta_access_token;
  const o = st.orders;
  const src = o.source;

  const upload = (dry: boolean) => run(dry ? 'file' : 'import', async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('dry_run', dry ? 'true' : 'false');
    if (Object.keys(mapping).length) fd.append('mapping', JSON.stringify(mapping));
    const r: FileResult = await call(`/${clientId}/orders-file`, { method: 'POST', body: fd });
    setFres(r);
    setMapping(r.mapping || {});
    if (!dry && r.ok) {
      flash(`${r.orders} sipariş içe aktarıldı (${d(r.start)} – ${d(r.end)}).`);
      setFile(null); setFres(null); setMapping({});
      if (fileInput.current) fileInput.current.value = '';
      await load(); onChanged?.();
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {err && <Card pad={14} style={{ borderColor: T.red }}><span style={{ color: T.redText, fontSize: 13 }}>{err}</span></Card>}

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SectionHeader title="Veri Durumu" right={msg ? <span style={{ fontSize: 12, color: T.greenText, fontWeight: 700 }}>{msg}</span> : undefined} />
        <p style={{ ...hint, marginTop: -8 }}>
          Bir kaynak bağlandığında ya da rapor oluşturulduğunda geçmiş verisi arka planda kendiliğinden yüklenir (reklamlarda 400 gün,
          siparişlerde 120 gün). Sonrasında veriler 6 saatte bir güncellenir. Bağlı olmayan kaynakların bölümleri raporda gizlenir.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="outline" disabled={busy === 'check'} onClick={() => run('check', async () => {
            const r = await call(`/${clientId}/check`, { method: 'POST' });
            setChecks(r.results);
            if (!Object.keys(r.results).length) flash('Test edilecek bağlı kaynak yok.');
          })}>{busy === 'check' ? 'Test ediliyor…' : 'Bağlantıları Test Edin'}</Button>
          <Button variant="outline" disabled={busy === 'sync' || running} onClick={() => run('sync', async () => {
            const r = await call(`/${clientId}/sync`, { method: 'POST', body: JSON.stringify({}) });
            setSt(r);
            flash(r.started?.length ? `${r.started.map((s: string) => SOURCE_KEYS[s] || s).join(', ')} yükleniyor.` : 'Yüklenecek bağlı kaynak yok.');
          })}>{running ? 'Yükleniyor…' : 'Geçmiş Veriyi Yeniden Yükleyin'}</Button>
        </div>
      </Card>

      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', alignItems: 'start' }}>
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <SectionHeader title="Reklam ve Analitik" />
          <p style={{ ...hint, marginTop: -8, marginBottom: 4 }}>Özet, Reklam Kanalları, Trafik ve Dönüşüm ile Ürünler (Shop) bölümlerini besler.</p>

          <Row title="Google Ads" connected={!!st.fields.gads_customer_id}>
            <label style={labelStyle}>Müşteri Kimliği
              <input style={inputStyle} inputMode="numeric" placeholder="123-456-7890" value={ads.gads_customer_id}
                onChange={(e) => setAds({ ...ads, gads_customer_id: e.target.value })} />
            </label>
            <p style={hint}>Hesap, Poyais Digital yönetici hesabına (MCC) bağlı olmalı.</p>
            {st.fields.gads_customer_id && <span style={{ fontSize: 12, color: T.textMid }}>{cov(st.coverage.google_ads)}</span>}
            <JobLine job={st.jobs.google_ads} /><CheckLine c={checks.google_ads} />
          </Row>

          <Row title="Meta Ads" connected={!!st.fields.meta_account_id && st.fields.meta_token}>
            <label style={labelStyle}>Reklam Hesabı Kimliği
              <input style={inputStyle} inputMode="numeric" placeholder="act_ olmadan, ör. 238238991171684" value={ads.meta_account_id}
                onChange={(e) => setAds({ ...ads, meta_account_id: e.target.value })} />
            </label>
            <label style={labelStyle}>Erişim Anahtarı (isteğe bağlı)
              <input style={inputStyle} type="password" autoComplete="off" value={ads.meta_access_token}
                placeholder={st.fields.meta_own_token ? 'Kayıtlı · değiştirmek için yenisini girin' : 'Boş bırakılırsa panel kullanıcısının Meta bağlantısı kullanılır'}
                onChange={(e) => setAds({ ...ads, meta_access_token: e.target.value })} />
            </label>
            {st.fields.meta_account_id && !st.fields.meta_token && (
              <p style={{ ...hint, color: T.redText }}>Bu hesap için Meta erişimi bulunamadı. Müşteriye bağlı bir panel kullanıcısının Meta hesabını bağlayın ya da erişim anahtarı girin.</p>
            )}
            {st.fields.meta_account_id && <span style={{ fontSize: 12, color: T.textMid }}>{cov(st.coverage.meta_ads)}</span>}
            <JobLine job={st.jobs.meta_ads} /><CheckLine c={checks.meta_ads} />
          </Row>

          <Row title="Google Analytics 4" connected={!!st.fields.ga4_property_id}>
            <label style={labelStyle}>Property ID
              <input style={inputStyle} inputMode="numeric" placeholder="ör. 339274680" value={ads.ga4_property_id}
                onChange={(e) => setAds({ ...ads, ga4_property_id: e.target.value })} />
            </label>
            <p style={hint}>
              {st.ga4_oauth ? 'Müşterinin kendi Google bağlantısı kullanılıyor. ' : ''}
              GA4 canlı sorgulanır.<br />
              Erişim için GA4 &gt; Yönetici &gt; Mülk erişim yönetimi'nden{' '}
              {st.service_account ? <><b style={{ color: T.textMid, wordBreak: 'break-all' }}>{st.service_account}</b> adresini</> : 'servis hesabını'} Görüntüleyici olarak ekleyin.
            </p>
            <JobLine job={st.jobs.ga4} /><CheckLine c={checks.ga4} />
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
            <Button variant="solid" disabled={!adsDirty || busy === 'save'} style={{ color: '#fff' }}
              onClick={() => save({ ...ads, meta_access_token: ads.meta_access_token || undefined }, 'Kaydedildi; değişen kaynakların geçmişi yükleniyor.')}>
              {busy === 'save' ? 'Kaydediliyor…' : 'Kaydedin'}
            </Button>
          </div>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SectionHeader title="Sipariş Verisi" />
            <p style={{ ...hint, marginTop: -8 }}>
              "Ürünler (Tümü)" bölümünü besler: web sitesi ve pazaryerleri dahil tüm kanallarda ürün bazında satış. Müşterinin kullandığı altyapıyı seçin.
            </p>
            <div role="radiogroup" aria-label="Sipariş kaynağı" style={{ display: 'flex', gap: 4, background: T.trackBg, borderRadius: 11, padding: 4, flexWrap: 'wrap' }}>
              {[['', 'Yok'], ...Object.entries(o.sources)].map(([k, label]) => (
                <button key={k || 'none'} type="button" role="radio" aria-checked={src === k} disabled={busy === 'save'}
                  onClick={() => { if (src !== k && (!st.coverage.orders || window.confirm('Sipariş kaynağı değişirse mevcut satış verisi silinir. Devam edilsin mi?'))) save({ order_source: k }, 'Sipariş kaynağı güncellendi.'); }}
                  style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: src === k ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                    background: src === k ? T.surface : 'transparent', color: src === k ? T.text : T.textMidAlt, border: 'none',
                    boxShadow: src === k ? '0 1px 2px rgba(20,22,43,.1)' : 'none' }}>{label}</button>
              ))}
            </div>

            {src === 'pixa' && (o.pixa.connected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Badge color={T.green}>Bağlı</Badge>
                <span style={{ fontSize: 13, color: T.textMid }}>{o.pixa.host}{o.pixa.user ? ` · ${o.pixa.user}` : ''}</span>
                <Button variant="ghost" disabled={busy === 'pixa'} onClick={() => window.confirm('Pixa bağlantısı kaldırılsın mı? Panelin Pixa sayfaları da bu müşteri için kapanır.')
                  && run('pixa', async () => { apply(await call(`/${clientId}/pixa`, { method: 'DELETE' })); onChanged?.(); })}>Bağlantıyı Kaldırın</Button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={labelStyle}>Pixa Adresi<input style={inputStyle} placeholder="ör. p1234.pixasoftwebservices.com" value={pixa.host} onChange={(e) => setPixa({ ...pixa, host: e.target.value })} /></label>
                <label style={labelStyle}>E-posta<input style={inputStyle} type="email" value={pixa.email} onChange={(e) => setPixa({ ...pixa, email: e.target.value })} /></label>
                <label style={labelStyle}>API Token<input style={inputStyle} type="password" autoComplete="off" value={pixa.api_token} onChange={(e) => setPixa({ ...pixa, api_token: e.target.value })} /></label>
                <div><Button variant="solid" style={{ color: '#fff' }} disabled={busy === 'pixa' || !pixa.host || !pixa.email || !pixa.api_token}
                  onClick={() => run('pixa', async () => {
                    apply(await call(`/${clientId}/pixa`, { method: 'POST', body: JSON.stringify(pixa) }));
                    setPixa({ host: '', email: '', api_token: '' }); flash('Pixa bağlandı; sipariş geçmişi yükleniyor.'); onChanged?.();
                  })}>{busy === 'pixa' ? 'Bağlanıyor…' : "Pixa'yı Bağlayın"}</Button></div>
              </div>
            ))}

            <div style={{ display: 'grid', gap: 10, borderTop: `1px solid ${T.border || '#e4e2da'}`, paddingTop: 14 }}>
              <p style={{ ...hint, fontWeight: 700, margin: 0 }}>Boyner API</p>
              {o.boyner?.connected ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Badge color={T.green}>Bağlı</Badge>
                  <span style={{ fontSize: 13, color: T.textMid }}>Satıcı ID: {o.boyner.seller_id}</span>
                  <Button variant="ghost" disabled={busy === 'boyner'} onClick={() => run('boyner', async () => {
                    const r = await call(`/${clientId}/boyner/sync`, { method: 'POST' });
                    flash(`Boyner iadeleri güncellendi: ${r.claims} talep, ${r.counted} iade sayıldı.`);
                  })}>İadeleri Şimdi Çekin</Button>
                  <Button variant="ghost" disabled={busy === 'boyner'} onClick={() => window.confirm('Boyner API bilgileri kaldırılsın mı?')
                    && run('boyner', async () => { apply(await call(`/${clientId}/boyner`, { method: 'DELETE' })); })}>Bilgileri Kaldırın</Button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  <label style={labelStyle}>Satıcı ID<input style={inputStyle} value={boyner.seller_id} onChange={(e) => setBoyner({ ...boyner, seller_id: e.target.value })} /></label>
                  <label style={labelStyle}>API Key<input style={inputStyle} autoComplete="off" value={boyner.api_key} onChange={(e) => setBoyner({ ...boyner, api_key: e.target.value })} /></label>
                  <label style={labelStyle}>API Secret<input style={inputStyle} type="password" autoComplete="off" value={boyner.api_secret} onChange={(e) => setBoyner({ ...boyner, api_secret: e.target.value })} /></label>
                  <div><Button variant="solid" style={{ color: '#fff' }} disabled={busy === 'boyner' || !boyner.seller_id || !boyner.api_key || !boyner.api_secret}
                    onClick={() => run('boyner', async () => {
                      apply(await call(`/${clientId}/boyner`, { method: 'POST', body: JSON.stringify(boyner) }));
                      setBoyner({ seller_id: '', api_key: '', api_secret: '' }); flash('Boyner API bağlandı, iadeler çekildi.');
                    })}>{busy === 'boyner' ? 'Kaydediliyor…' : 'Boyner API Bilgilerini Kaydedin'}</Button></div>
                </div>
              )}
              <p style={hint}>Pixa Boyner iadelerini aktarmadığı için iadeler Boyner API'den 6 saatte bir otomatik çekilir (onaylı, kargodaki ve incelenen iade talepleri; sipariş tarihine göre). API bilgileri şifreli saklanır.</p>
            </div>

            {src === 'shopify' && (o.shopify.connected ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Badge color={T.green}>Bağlı</Badge>
                  <span style={{ fontSize: 13, color: T.textMid }}>{o.shopify.name || o.shopify.shop}</span></div>
                <p style={hint}>Shopify yalnızca son 60 günün siparişlerini verir; daha eski dönem için sipariş dışa aktarım dosyası yükleyebilirsiniz.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={labelStyle}>Mağaza Adresi<input style={inputStyle} placeholder="ör. markaniz.myshopify.com" value={shop} onChange={(e) => setShop(e.target.value)} /></label>
                <div><Button variant="solid" style={{ color: '#fff' }} disabled={busy === 'shopify' || !shop.trim()}
                  onClick={() => run('shopify', async () => {
                    const r = await call(`/${clientId}/shopify`, { method: 'POST', body: JSON.stringify({ shop }) });
                    window.location.href = r.auth_url;
                  })}>{busy === 'shopify' ? 'Yönlendiriliyor…' : "Shopify'ı Bağlayın"}</Button></div>
                <p style={hint}>Shopify'da uygulama onayından sonra bu sayfaya dönersiniz; sipariş geçmişi kendiliğinden yüklenir.</p>
              </div>
            ))}

            {src === 'file' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={hint}>
                  Ticimax, İkas, T-Soft, Akinon, SAP gibi altyapılardan sipariş raporunu <b>ürün satırı bazında</b> Excel (.xlsx) ya da CSV olarak
                  dışa aktarıp yükleyin. Sütunlar otomatik tanınır, gerekirse aşağıdan düzeltin. Aynı tarih aralığı yeniden yüklenirse eskisinin
                  yerine geçer; raporu güncel tutmak için her hafta ya da ay başında yeni dosya yükleyin. Müşteri adı, adres gibi sütunlar okunmaz.
                </p>
                <input ref={fileInput} type="file" accept=".xlsx,.xlsm,.csv,.txt" aria-label="Sipariş dosyası"
                  onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); setFres(null); setMapping({}); }}
                  style={{ fontSize: 13, color: T.textMid }} />
                {file && !fres && <div><Button variant="outline" disabled={busy === 'file'} onClick={() => upload(true)}>{busy === 'file' ? 'Okunuyor…' : 'Dosyayı Kontrol Edin'}</Button></div>}
                {fres && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderRadius: 12, background: T.subtleBg }}>
                    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
                      {Object.entries(fres.fields).map(([k, label]) => (
                        <label key={k} style={labelStyle}>{label}{fres.required.includes(k) ? ' *' : ''}
                          <select style={inputStyle} value={mapping[k] || ''} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })}>
                            <option value="">— yok —</option>
                            {fres.headers.filter(Boolean).map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                    {fres.ok ? (
                      <div style={{ fontSize: 13, color: T.textMid, lineHeight: '20px' }}>
                        <b style={{ color: T.text }}>{fres.orders} sipariş · {fres.lines} satır</b> · {d(fres.start)} – {d(fres.end)}<br />
                        Satış {tl(fres.revenue)} · iadeler hariç {tl(fres.net_revenue)} · kanallar: {fres.channels?.join(', ')}
                        {fres.skipped && Object.values(fres.skipped).some(Boolean) && (
                          <><br /><span style={{ color: T.textDim }}>Atlanan satır: {Object.entries(fres.skipped).filter(([, n]) => n).map(([k, n]) => `${n} ${k === 'iptal' ? 'iptal' : k === 'tarih' ? 'tarihsiz' : 'kodsuz'}`).join(', ')}</span></>
                        )}
                        {fres.preview && fres.preview.length > 0 && (
                          <div style={{ marginTop: 8, fontSize: 12, color: T.textDim }}>
                            Örnek: {fres.preview.slice(0, 3).map((p) => `${d(p.date)} · ${p.channel} · ${p.code} · ${p.qty} adet · ${tl(p.amount)}`).join(' | ')}
                          </div>
                        )}
                      </div>
                    ) : <span style={{ fontSize: 13, color: T.redText }}>{fres.error}</span>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button variant="outline" disabled={busy === 'file'} onClick={() => upload(true)}>{busy === 'file' ? 'Okunuyor…' : 'Eşleşmeyle Yeniden Kontrol Edin'}</Button>
                      <Button variant="solid" style={{ color: '#fff' }} disabled={!fres.ok || busy === 'import'} onClick={() => upload(false)}>
                        {busy === 'import' ? 'İçe aktarılıyor…' : 'İçe Aktarın'}</Button>
                    </div>
                  </div>
                )}
                {o.imports.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>Son Yüklemeler</span>
                    {o.imports.map((f) => (
                      <span key={f.id} style={{ fontSize: 12, color: T.textDim }}>
                        {ts(f.created_at)} · {f.filename} · {d(f.start)} – {d(f.end)} · {f.orders} sipariş · {tl(f.revenue)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {src && (
              <label style={labelStyle}>Model Kodu
                <select style={inputStyle} value={o.code_rule} disabled={busy === 'save'} onChange={(e) => save({ code_rule: e.target.value }, 'Model kodu kuralı güncellendi.')}>
                  {Object.entries(o.code_rules).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </label>
            )}
            {src && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: T.textMid }}>{st.coverage.orders ? `Veri: ${d(st.coverage.orders.start)} – ${d(st.coverage.orders.end)}` : 'Henüz satış verisi yok'}</span>
                {src !== 'file' && <JobLine job={st.jobs.orders} />}
                {o.last_error && <span style={{ fontSize: 12, color: T.redText }}>Son Güncelleme Hatası: {o.last_error}</span>}
                <CheckLine c={checks.orders} />
              </div>
            )}
          </Card>

          <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <SectionHeader title="Web Sitesi" />
            <p style={{ ...hint, marginTop: -8 }}>
              Site Shopify ile kuruluysa ürün kataloğu buradan okunur: ürün tablolarında model kodu görünür, koda tıklayınca ürün sayfası açılır.
            </p>
            <label style={labelStyle}>Adres
              <input style={inputStyle} placeholder="ör. www.markaniz.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </label>
            <span style={{ fontSize: 12, color: T.textMid }}>
              {st.catalog.products ? `${st.catalog.products} ürün okundu · ${ts(st.catalog.updated)} UTC` : 'Katalog okunmadı (site Shopify değilse bu normaldir).'}
            </span>
            <JobLine job={st.jobs.catalog} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="solid" style={{ color: '#fff' }} disabled={website === st.fields.website || busy === 'save'}
                onClick={() => save({ website }, 'Kaydedildi; katalog okunuyor.')}>Kaydedin</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
